import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ca } from '@cian/analytics';

import { selectAnalytics } from '../../selectors/analytics';
import { useCurrentBreadCrumbs } from '../useCurrentBreadCrumbs';
import { useDeviceType } from '@cian/ui-kit';

interface IUserData {
  abGroup?: number;
  email?: string;
  type?: string;
  userId?: number;
  cianUserId?: number;
}

interface IPageData {
  breadCrumbs?: string[];
  pageType?: string;
  siteType?: 'mobile' | 'tablet' | 'desktop';
  extra?: Record<string, string | string[] | number | number[]>;
}

interface IPartnerData {
  CriteoEmail?: string;
  CriteoNewCustomer?: number;
}

interface IProductData {
  id?: number;
}

export interface IEventTrackingData {
  category?: string;
  action?: string;
  label?: string;
  user?: IUserData;
  page?: IPageData;
  partner?: IPartnerData;
  product?: IProductData;
}

/**
 * Создает HOF-функцию трекера событий для 'ca' с предзаполненными данными
 */
export const useEventTracking = (eventTrackerData?: IEventTrackingData) => {
  const { user } = useSelector(selectAnalytics);
  const breadCrumbs = useCurrentBreadCrumbs();
  const deviceType = useDeviceType();

  const getPreparedData = useCallback(
    (data: IEventTrackingData = {}): IEventTrackingData => {
      const partner = {
        ...(user?.criteoEmail ? { CriteoEmail: user.criteoEmail } : {}),
        ...(user?.criteoNewCustomer ? { CriteoNewCustomer: user.criteoNewCustomer } : {}),
        ...eventTrackerData?.partner,
        ...data?.partner,
      };
      const product = {
        ...eventTrackerData?.product,
        ...data?.product,
      };

      return {
        category: data?.category || eventTrackerData?.category,
        action: data?.action || eventTrackerData?.action,
        label: data?.label || eventTrackerData?.label,
        user: {
          abGroup: Number(user?.abGroup) || 100,
          ...(user?.email ? { email: user.email } : {}),
          type: user?.userType || 'not_authorized',
          ...(user?.realtyUserId ? { userId: Number(user.realtyUserId) } : {}),
          ...(user?.userId ? { cianUserId: Number(user.userId) } : {}),
          ...eventTrackerData?.user,
          ...data?.user,
        },
        page: {
          ...eventTrackerData?.page,
          ...data?.page,
          extra: {
            ...eventTrackerData?.page?.extra,
            ...data?.page?.extra,
          },
          breadCrumbs: breadCrumbs.map(breadCrumb => breadCrumb.breadCrumbName),
          pageType: 'Magazine',
          siteType: deviceType === 'phone' ? 'mobile' : deviceType,
        },
        ...(Object.keys(partner).length ? { partner } : {}),
        ...(Object.keys(product).length ? { product } : {}),
      };
    },
    [
      breadCrumbs,
      deviceType,
      eventTrackerData,
      user?.abGroup,
      user?.criteoEmail,
      user?.criteoNewCustomer,
      user?.email,
      user?.realtyUserId,
      user?.userId,
      user?.userType,
    ],
  );

  const trackEvent = useCallback(
    (data: IEventTrackingData = {}) => {
      ca('event', {
        name: 'oldevent',
        ...getPreparedData(data),
      });
    },
    [getPreparedData],
  );

  const trackPageView = useCallback(
    (data: IEventTrackingData = {}) => {
      const { user, page, partner } = getPreparedData(data);

      ca('pageviewSite', { user, page, partner });
    },
    [getPreparedData],
  );

  return {
    trackEvent,
    trackPageView,
  };
};
